/**
 * Audit row N17's client door onto class formations, who stands where.
 * classFormations/{offeringId} sits in a collection no Firestore rule names,
 * so the rules' default deny refuses every client read and write of it; every
 * reader and writer goes through the classFormations Function
 * (netlify/functions/lib/classFormations.mjs holds the decisions). A Jr Staff
 * on her dancer sign-in reads the same answer through the dancer portal
 * (getDancerPortalClassFormations in dancerSession.ts).
 *
 * No listener: one read when the section opens, and every save hands back the
 * class's fresh list, so the screen never re-reads to see its own change.
 */

export interface FormationRow {
  /** Only an editor gets the id; a reader gets the name alone. */
  studentId?: string;
  name: string;
  note: string;
  /** False once the dancer has left the class roster. */
  inClass: boolean;
}

export interface ClassFormation {
  id: string;
  title: string;
  notes: string;
  rev: number;
  updatedAt: string;
  rows: FormationRow[];
}

export interface ClassFormationsView {
  canEdit: boolean;
  formations: ClassFormation[];
}

export interface FormationDraft {
  id?: string;
  title: string;
  notes: string;
  rows: Array<{ studentId: string; note: string }>;
}

export class ClassFormationsRequestError extends Error {
  code: string;
  constructor(code: string) {
    super(code || 'class_formations_failed');
    this.code = code;
  }
}

/** Limits the server holds; the editor mirrors them so a save is never refused for length. */
export const FORMATION_TITLE_MAX = 80;
export const FORMATION_NOTES_MAX = 1000;
export const FORMATION_ROW_NOTE_MAX = 80;
export const FORMATION_ROWS_MAX = 80;

export function classFormationsErrorMessage(code: string): string {
  switch (code) {
    case 'not_allowed':
      return 'Only this class\'s teachers and Jr Staff, Directors, Managers, company staff and the front desk can see its formations.';
    case 'stale_edit':
      return 'Someone else changed this formation since you opened it, so nothing was saved. Tap Cancel to see their version, then edit again.';
    case 'not_in_class':
      return 'One of these dancers is no longer in this class. Take her out of the lineup, then save again.';
    case 'duplicate_dancer':
      return 'A dancer is in this lineup twice. Take one out, then save again.';
    case 'title_required':
      return 'Give the formation a name first.';
    case 'too_many_formations':
      return 'This class already has the most formations it can hold. Delete one you no longer use, then add this one.';
    case 'not_found':
      return 'That formation was deleted since you opened it.';
    case 'class_closed':
      return 'This class is no longer running, so its formations are closed.';
    case 'too_many_requests':
      return 'That was a lot of saves in a row. Wait a few minutes, then try again.';
    case 'too_many_reads':
      return 'Too many formations opened in a row. Try again in a few minutes.';
    default:
      return 'Could not reach the formations. Try again.';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Read defensively: a malformed row is dropped, never thrown. */
export function formationsViewFrom(payload: unknown): ClassFormationsView {
  const data = isRecord(payload) ? payload : {};
  const list = Array.isArray(data.formations) ? data.formations : [];
  return {
    canEdit: data.canEdit === true,
    formations: list.filter(isRecord).map((f) => ({
      id: typeof f.id === 'string' ? f.id : '',
      title: typeof f.title === 'string' ? f.title : 'Formation',
      notes: typeof f.notes === 'string' ? f.notes : '',
      rev: typeof f.rev === 'number' ? f.rev : 1,
      updatedAt: typeof f.updatedAt === 'string' ? f.updatedAt : '',
      rows: (Array.isArray(f.rows) ? f.rows : []).filter(isRecord).map((r) => ({
        ...(typeof r.studentId === 'string' ? { studentId: r.studentId } : {}),
        name: typeof r.name === 'string' ? r.name : 'Dancer',
        note: typeof r.note === 'string' ? r.note : '',
        inClass: r.inClass !== false,
      })),
    })).filter((f) => f.id),
  };
}

/**
 * firebase/auth and ./functionSecurity load DYNAMICALLY, for the reason
 * classSkillsClient.ts gives: a render-only harness mocks ./firebase with
 * Firestore alone, and a static import of the App Check chain would fail at
 * module load instead of inside this caught async call.
 */
async function callClassFormations(body: Record<string, unknown>): Promise<ClassFormationsView> {
  const [{ getAuth }, { functionHeaders }] = await Promise.all([
    import('firebase/auth'),
    import('./functionSecurity'),
  ]);
  const user = getAuth().currentUser;
  if (!user) throw new ClassFormationsRequestError('sign_in_required');
  const response = await fetch('/.netlify/functions/classFormations', {
    method: 'POST',
    headers: await functionHeaders(user),
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok || data.error) {
    const code = data.error === 'too_many_requests' && body.action === 'read' ? 'too_many_reads' : data.error ?? '';
    throw new ClassFormationsRequestError(code);
  }
  return formationsViewFrom(data);
}

export function readClassFormations(offeringId: string): Promise<ClassFormationsView> {
  return callClassFormations({ action: 'read', offeringId });
}

export function saveClassFormation(
  offeringId: string,
  draft: FormationDraft,
  expectedRev: number | null,
): Promise<ClassFormationsView> {
  return callClassFormations({
    action: 'save',
    offeringId,
    formation: draft,
    ...(draft.id ? { expectedRev } : {}),
  });
}

export function deleteClassFormation(offeringId: string, formationId: string): Promise<ClassFormationsView> {
  return callClassFormations({ action: 'delete', offeringId, formationId });
}
